/* eslint-disable max-statements */
import { useSelector } from 'react-redux';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { LoadingSpinner } from '../../loadingSpinner/loadingspinner';
import {
    useGetScorecardByIDQuery,
    useGetUserByIDQuery,
    useGetUserByNameQuery,
} from '../../scoreCardFilter/scoreCardFilterSlice';

import { selectCurrentEmpid } from '../../../pages/login/authSlice';
import { removeSelectedLocalStorageKeys } from '../../../utils/clearLocalStorageKeys';

type Employee = {
    name: string;
    id: string;
    supId: string;
}[];

type associateLookupProps = {
    setAgentNumber?: React.Dispatch<any>;
    setAgentName?: (e: any) => void;
    menuName: string;
    ipInputMonth: number;
    ipInputYear: number;
    ipSetInputScorecard: React.Dispatch<React.SetStateAction<string | number>>;
    resetDate: () => void;
    filterDisabled?: boolean;
    setScorecardOptions: React.Dispatch<any>;
    setInputScorecardVersion: React.Dispatch<any>;
};

export const AssociateLookup = ({
    setAgentNumber,
    setAgentName,
    menuName,
    ipInputMonth,
    ipInputYear,
    ipSetInputScorecard,
    resetDate,
    filterDisabled = false,
    setScorecardOptions,
    setInputScorecardVersion,
}: associateLookupProps) => {
    const [inputMonth, setMonth] = useState(new Date().getMonth() + 1);
    const [inputYear, setYear] = useState(new Date().getFullYear());
    const [agent, setAgent] = useState('');
    const [inputNameOrID, setInputNameOrID] = useState<number | string>('');
    const [selectedScorecard, setSelectedScorecard] = useState<number | string>('');
    const [associateOracleId, setAssociateOracleId] = useState<string>('');
    const [selectedID, setSelectedId] = useState<string>('');
    const [associateName, setAssociateName] = useState<string>('');
    const [listOfUsers, setListOfUsers] = useState<Employee>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const loggedEmpNum = useSelector(selectCurrentEmpid);

    const {
        data: associateIdData,
        isFetching: associateIdFetching,
        isSuccess: idFound,
    } = useGetUserByIDQuery(
        {
            filterParam: associateOracleId,
            searcherEmpNo: loggedEmpNum,
            getAllEmps: true,
        },
        { skip: !associateOracleId || !loggedEmpNum }
    );
    const {
        data: associateNameData,
        isFetching: associateNameFetching,
        isSuccess: nameFound,
    } = useGetUserByNameQuery(
        {
            filterParam: associateName,
            searcherEmpNo: loggedEmpNum,
            getAllEmps: true,
        },
        { skip: !associateName || !loggedEmpNum }
    );
    const { data: scorecardIdData, isFetching: scorecardIdFetching } = useGetScorecardByIDQuery(
        {
            employeenumber: agent,
            year: inputYear,
            month: inputMonth,
        },
        { skip: !agent || !inputYear || !inputMonth }
    );

    const resetAllFilters = () => {
        // window.localStorage.clear();
        removeSelectedLocalStorageKeys([
            'ScFilDdlParClient',
            'ScFilDdlClient',
            'ScFilDdlProgram',
            'ScFilDdlProject',
            'ScFilDdlCountry',
            'ScFilDdlSite',
            'ScFilDdlScorecard',
            'ScFilDdlScorecardId',
            'ScFilDdlScorecardName',
            'SCfilDate',
        ]);
        setSelectedId('');
        setAgent('');
        setSelectedScorecard('');
        setInputScorecardVersion('');
        setMonth(new Date().getMonth() + 1);
        setYear(new Date().getFullYear());

        setAssociateOracleId('');
        setAssociateName('');
        if (setAgentName) {
            setAgentName('');
        }
        setListOfUsers([]);
        setInputNameOrID('');
        setScorecardOptions([]);
        resetDate();
    };

    const submitSelectedUser = () => {
        if (selectedID !== null && selectedID !== '') {
            setSelectedUser();
        } else if ((selectedID === null || selectedID === '') && listOfUsers.length > 0) {
            setAgent(listOfUsers[0].id);
            if (setAgentNumber) setAgentNumber(listOfUsers[0].id);
            if (setAgentName) setAgentName(listOfUsers[0].name);
            setValidationAgentName(listOfUsers[0]?.name);
        } else {
            toast.error('No user selected');
        }
    };

    const setSelectedUser = () => {
        setAgent(selectedID);
        try {
            const pickedAgent = listOfUsers?.filter((user) => {
                if (user.id !== '') return user.id.toString() === selectedID.toString();
                else throw new Error('error');
            });
            if (pickedAgent.length > 0) {
                setValidationAgentName(pickedAgent[0]?.name);
            }
        } catch (err: any) {
            toast.error('No user selected');
        }
    };

    const setValidationAgentName = (agentName: string) => {
        if (menuName === 'EdsDashboard' && setAgentName) setAgentName(agentName);
        if (menuName === 'ScorecardRules' && setAgentName) setAgentName(agentName);
    };

    useEffect(() => {
        if (idFound && associateIdData?.data.length > 0) {
            const arrayOfUsers: Employee = associateIdData?.data.map((item: any) => ({
                name: item?.last_name.concat(' ', item?.first_name, ' (', item?.employee_number, ')'),
                id: item?.employee_number,
                supId: item?.supervisor_number,
            }));
            setSelectedId('');
            setListOfUsers(arrayOfUsers);
        } else {
            setListOfUsers([]);
            if (setAgentNumber) setAgentNumber('');
        }
    }, [associateOracleId, idFound, associateIdData]);

    useEffect(() => {
        setMonth(ipInputMonth);
        setYear(ipInputYear);
    }, [ipInputMonth, ipInputYear]);

    useEffect(() => {
        setSelectedId('');
        const value = inputNameOrID.toString();
        if (/^\d+$/.test(value)) {
            if (value.length >= 7) {
                setListOfUsers([]);
                setAssociateOracleId(value);
                setSelectedId(value);
                setSelectedScorecard('');
                if (menuName === 'EdsDashboard' && setAgentNumber) setAgentNumber(value);
            } else {
                setListOfUsers([]);
                if (setAgentNumber) setAgentNumber('');
            }
        } else if (value.trim().length >= 3) {
            setListOfUsers([]);
        } else {
            setAssociateName('');
            setListOfUsers([]);
        }
    }, [inputNameOrID]);

    useEffect(() => {
        if (associateNameData?.data.length > 0) {
            const arrayOfUsers: Employee = associateNameData?.data.map((item: any) => ({
                name: item?.first_name.concat(' ', item?.last_name, ' (', item?.employee_number, ')'),
                id: item?.employee_number,
                supId: item?.supervisor_number,
            }));
            setListOfUsers(arrayOfUsers);
        }
    }, [associateName, nameFound]);

    useEffect(() => {
        if (setAgentNumber) {
            setAgentNumber(selectedID);
        }
    }, [selectedID]);

    useEffect(() => {
        ipSetInputScorecard(selectedScorecard);
    }, [selectedScorecard]);

    useEffect(() => {
        if (!selectedID && !agent) {
            return;
        }
        if (scorecardIdData?.data.length > 0) {
            setScorecardOptions(scorecardIdData?.data);
            if (window.localStorage.ScFilDdlScorecard) {
                setSelectedScorecard(parseInt(window.localStorage.ScFilDdlScorecard, 10));
            }
        } else setScorecardOptions([]);
    }, [scorecardIdData, selectedID]);

    useEffect(() => {
        if (scorecardIdFetching || associateIdFetching || associateNameFetching) {
            setLoading(true);
        } else setLoading(false);
    }, [scorecardIdFetching, associateIdFetching, associateNameFetching]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setInputNameOrID(value);

        if (!value || value.trim().length === 0) {
            setAssociateOracleId('');
            setAssociateName('');
            setListOfUsers([]);
            return;
        }

        if (/^\d+$/.test(value)) {
            if (value.length >= 7) {
                setAssociateOracleId(value);
                setAssociateName('');
            } else {
                setAssociateOracleId('');
                setListOfUsers([]);
            }
        } else if (value.trim().length >= 3) {
            setAssociateName(value);
            setAssociateOracleId('');
        } else {
            setAssociateName('');
            setListOfUsers([]);
        }

        setSelectedId('');
        setSelectedScorecard('');
    };

    return (
        <>
            <div
                className="d-flex flex-wrap align-items-center justify-content-between"
                style={{ marginTop: '1rem', gap: '1rem' }}
            >
                <div className="d-flex flex-wrap align-items-center gap-3">
                    <div style={{ width: '280px' }}>
                        <h6>Associate Name or Oracle ID</h6>
                        <InputText
                            style={{ borderRadius: '25px', width: '100%' }}
                            disabled={filterDisabled}
                            placeholder="Search by Name or Oracle ID"
                            value={inputNameOrID}
                            onChange={handleInputChange}
                        />
                    </div>

                    <div style={{ width: '280px' }}>
                        <h6>Select User</h6>
                        <Dropdown
                            id="AssociateLookupUser"
                            disabled={filterDisabled}
                            value={selectedID}
                            options={listOfUsers}
                            optionLabel="name"
                            optionValue="id"
                            placeholder="Select User"
                            style={{ width: '100%' }}
                            onChange={(e: any) => {
                                setSelectedId(e.value);
                                submitSelectedUser();
                            }}
                        />
                    </div>
                </div>

                <div>
                    <Button
                        type="button"
                        label="Reset"
                        style={{ top: '-85px' }}
                        disabled={filterDisabled}
                        className="btn-secondary"
                        onClick={resetAllFilters}
                    />
                </div>
            </div>
            <span>{loading ? <LoadingSpinner /> : ''}</span>
            <br />
        </>
    );
};
